import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Who a captain wants messaged about games. Ticks win; none ticked means
 *  the system works it out from the last two months of games. */
export const ActiveSquadSheet = ({ teamId, teamName, open, onClose }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !teamId) return;
    setData(null);
    axios.get(`${API}/section/whatsapp/team/${teamId}/members`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not load the squad");
        onClose();
      });
  }, [open, teamId]);

  const toggle = (userId) => {
    setData({
      ...data,
      using_ticks: true,
      members: data.members.map((m) =>
        m.user_id === userId ? { ...m, available: !m.available } : m),
    });
  };

  const save = async (ids) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/section/whatsapp/team/${teamId}/members`,
        { user_ids: ids });
      setData(r.data);
      toast.success(ids.length
        ? `${ids.length} marked active for ${teamName}`
        : "Back to working it out from recent games");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = (data?.members || []).filter((m) => m.available).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="active-squad-sheet">
        <DialogHeader>
          <DialogTitle>Who gets messaged — {teamName}</DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="py-10 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-400">
              {data.using_ticks
                ? `${activeCount} of ${data.members.length} marked active. Only these get game messages.`
                : "Nobody marked yet, so anyone who played or said yes in the last two months is counted active."}
            </p>
            <div className="space-y-1 mt-2">
              {data.members.map((m) => (
                <label key={m.user_id}
                  className="flex items-center gap-3 p-2 rounded hover:bg-slate-800/60 cursor-pointer"
                  data-testid={`active-row-${m.user_id}`}>
                  <Checkbox checked={!!m.available} onCheckedChange={() => toggle(m.user_id)}
                    data-testid={`active-tick-${m.user_id}`} />
                  <span className="flex-1 text-sm text-slate-200">{m.name}</span>
                  {!m.has_mobile && (
                    <span className="text-[10px] text-amber-400">no mobile</span>
                  )}
                  {m.opted_out && <span className="text-[10px] text-red-400">opted out</span>}
                  {m.muted && <span className="text-[10px] text-slate-400">muted</span>}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
              <Button size="sm" disabled={saving}
                onClick={() => save(data.members.filter((m) => m.available)
                  .map((m) => m.user_id))}
                data-testid="active-save-btn">
                {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Save
              </Button>
              <Button size="sm" variant="outline" disabled={saving}
                onClick={() => save(data.members.map((m) => m.user_id))}
                data-testid="active-all-btn">
                Mark everyone active
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-400" disabled={saving}
                onClick={() => save([])}
                data-testid="active-auto-btn">
                Work it out from recent games
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActiveSquadSheet;
